import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { mergePages, withCursor } from './paging';

export interface Resource<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * GET を 1 本読む。path が変わったら読み直し、古い応答は捨てる（環境を素早く切り替えた時に
 * development の結果が production の画面に出る、を防ぐ）。path = null なら読まない。
 * pollMs を渡すと定期的に読み直す（稼働ルームなど「今」を見る画面用）。
 */
export function useResource<T>(path: string | null, pollMs?: number): Resource<T> {
  const [state, setState] = useState<{ data: T | null; error: ApiError | null; loading: boolean }>({ data: null, error: null, loading: !!path });
  const [tick, setTick] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!path) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    api<T>(path)
      .then((data) => mine === seq.current && setState({ data, error: null, loading: false }))
      .catch((error: unknown) => {
        if (mine !== seq.current) return;
        const e = error instanceof ApiError ? error : new ApiError(0, 'client_error', String(error));
        setState({ data: null, error: e, loading: false });
      });
  }, [path, tick]);

  // path が変わったら前のデータは見せない（別環境・別プロジェクトの値の取り違え防止）
  useEffect(() => {
    setState({ data: null, error: null, loading: !!path });
  }, [path]);

  useEffect(() => {
    if (!pollMs || !path) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setTick((t) => t + 1);
    }, pollMs);
    return () => window.clearInterval(id);
  }, [pollMs, path]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** 「さらに読み込む」の状態（components/LoadMore.tsx に渡す） */
export interface MoreState {
  /** まだ次のページがあるか（サーバーの nextCursor が null でない） */
  has: boolean;
  load: () => void;
  loading: boolean;
  error: ApiError | null;
}

/**
 * ページ送りのある一覧を読む（2026-10-04 console/limits-ui・paging.ts の WHY）。
 * 最初のページは useResource と同じ（path が変わったら読み直し・古い応答は捨てる）。more.load で nextCursor の次のページを読み、
 * key の一覧の後ろに足す。最初のページを読み直したら（reload・path の変化）足した分は捨てる = 別環境の行が混ざらない。
 * 応答は { [key]: 行[], nextCursor: string | null } の形（nextCursor が無い古いサーバーなら「次は無い」と扱う）。
 */
export function usePagedResource<T extends { nextCursor?: string | null }, K extends keyof T>(path: string | null, key: K): Resource<T> & { more: MoreState } {
  const first = useResource<T>(path);
  const [extra, setExtra] = useState<unknown[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // 読み込み中に最初のページが変わったら、遅れて届いた次のページを捨てる（useResource の seq と同じ考え）
  const gen = useRef(0);

  useEffect(() => {
    gen.current++;
    setExtra([]);
    setError(null);
    setLoading(false);
    setCursor(first.data?.nextCursor ?? null);
  }, [first.data]);

  const load = useCallback(() => {
    if (!path || !cursor || loading) return;
    const mine = gen.current;
    setLoading(true);
    setError(null);
    api<T>(withCursor(path, cursor))
      .then((r) => {
        if (mine !== gen.current) return;
        setExtra((x) => [...x, ...(((r[key] as unknown[]) ?? []))]);
        setCursor(r.nextCursor ?? null);
      })
      .catch((e: unknown) => {
        if (mine !== gen.current) return;
        setError(e instanceof ApiError ? e : new ApiError(0, 'client_error', String(e)));
      })
      .finally(() => {
        if (mine === gen.current) setLoading(false);
      });
  }, [path, cursor, loading, key]);

  return { ...first, data: mergePages(first.data, key, extra), more: { has: Boolean(cursor), load, loading, error } };
}

/** 1 行で使える「コピーしました」状態 */
export function useCopy(timeout = 1600) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = useCallback(
    async (text: string, id = text) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // clipboard API が使えない環境（http の LAN アクセス等）向けのフォールバック
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(id);
      window.setTimeout(() => setCopied((c) => (c === id ? null : c)), timeout);
    },
    [timeout]
  );
  return { copied, copy };
}
